/* eslint-disable no-plusplus */
import React from 'react';
import _ from 'lodash';

import { convertPxToVw } from 'common/utils/helpers';
import { PIN } from 'common/constants';
import { BBUiConfig, BBModel } from 'types/story';

// TODO:
// ✅ move autoHeight logic to separated component
// ✅ handle case with selection pin (bottom / top)
// ✅ make also height reducing depending on content size
// ✅ add an option to preserve bbox of some element for do not affect on content height (use prev bbox)
// add an option to skip some element for do not affect on content height
// save data (convertPxToVw, and other like in ContentResize...)

type ChildrenData = {
	[id: string]: {
		id: string;
		pinType: typeof PIN[keyof typeof PIN];
		prev?: {
			elementBBoxG: Pick<DOMRect, 'top' | 'left' | 'bottom' | 'width' | 'height'>;
			elementBBoxL: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>;
		};
	};
};

type Props = {
	contentAlignY: 'top' | 'center' | 'bottom';
	uiConfig: BBUiConfig;
	children: React.ReactNode;
	contentInnerRef: React.RefObject<HTMLDivElement>;
};

const PINS = {
	TOP: [PIN.lt, PIN.t, PIN.rt, PIN.none] as string[],
	CENTER: [PIN.l, PIN.c, PIN.r] as string[],
	BOTTOM: [PIN.lb, PIN.b, PIN.rb] as string[],
};

export const AUTO_HEIGHT_ATTR = {
	name: 'data-auto-height',
	value: { none: undefined, preserve: 'preserve' },
} as const;

export default class AutoHeight extends React.Component<Props> {
	framesCounter: number = 0;

	rafID: number = 0;

	childrenData?: ChildrenData;

	componentDidMount() {
		window.requestAnimationFrame(this.onRAF);
	}

	componentWillUnmount() {
		window.cancelAnimationFrame(this.rafID);
	}

	getChildrenData = (): ChildrenData => {
		const { children } = this.props;
		this.childrenData = React.Children.map(children as { props: BBModel }[], (child: { props: BBModel }) => ({
			id: child.props.uiConfig.nodeProps.id,
			pinType: _.get(child, 'props.uiConfig.componentProps.other.pinType', ''),
		})).reduce((acc, current) => {
			acc[current.id] = current;
			acc[current.id].prev = this.childrenData?.[current.id]?.prev;
			return acc;
		}, {});

		return this.childrenData;
	};

	onRAF = () => {
		this.rafID = window.requestAnimationFrame(this.onRAF);
		// if (++this.framesCounter % (60 * 5) !== 0) return; // 1 frame per 5 sec
		// console.info('---');

		this.resizeContent();
	};

	getChildBBox = (element, data: ChildrenData[string]) => {
		const isPreserve = element.getAttribute(AUTO_HEIGHT_ATTR.name) === AUTO_HEIGHT_ATTR.value.preserve;
		const prevValues = data.prev;
		const styles = window.getComputedStyle(element);
		let elementBBoxG;
		let elementBBoxL;

		if (isPreserve && prevValues) {
			// Use prev values instead of current. Used usually to discard resize when some element is being animated.
			elementBBoxG = { ...prevValues.elementBBoxG };
			elementBBoxL = { ...prevValues.elementBBoxL };
		} else {
			elementBBoxG = element.getBoundingClientRect();
			elementBBoxL = {
				top: parseFloat(styles.top) || 0,
				left: parseFloat(styles.left) || 0,
				width: parseFloat(styles.width) || 0,
				height: parseFloat(styles.height) || 0,
			};
		}

		return { elementBBoxG, elementBBoxL, alignSelf: styles['align-self'] };
	};

	getContentBBox = (): {
		right: number;
		left: number;
		top: number;
		bottom: number;
		width: number;
		height: number;
	} | null => {
		if (!this.props.contentInnerRef.current) return null;
		const { contentAlignY } = this.props;
		const { children } = this.props.contentInnerRef.current;
		const contentInnerBBox = this.props.contentInnerRef.current.getBoundingClientRect();
		const childrenData = this.getChildrenData();
		let bounds;

		if (children.length) {
			// @ts-expect-error ts-migrate FIXME
			_.forEach(children, (element: HTMLElement) => {
				if (!childrenData[element.id]) return;

				const { pinType } = childrenData[element.id];
				const { elementBBoxG, elementBBoxL, alignSelf } = this.getChildBBox(element, childrenData[element.id]);

				childrenData[element.id].prev = {
					elementBBoxG,
					elementBBoxL,
				};

				// skip auto-stretched items (stretched vertically):
				if (alignSelf === 'stretch') {
					return;
				}

				switch (contentAlignY) {
					case 'top': {
						if (elementBBoxG.top < contentInnerBBox.top && PINS.TOP.includes(pinType)) return;
						if (elementBBoxG.bottom > contentInnerBBox.bottom && PINS.BOTTOM.includes(pinType)) return;

						let distance: number;
						if (PINS.CENTER.includes(pinType)) {
							distance = (Math.abs(elementBBoxL.top) + elementBBoxL.height / 2) * 2;
						} else {
							distance = Math.abs(elementBBoxL.top) + elementBBoxL.height;
						}

						if (!bounds) {
							bounds = {
								top: 0,
								right: 0,
								bottom: distance,
								left: 0,
							};
						}

						bounds.bottom = Math.max(bounds.bottom, distance);
						break;
					}

					case 'center': {
						if (elementBBoxG.top < contentInnerBBox.top && PINS.TOP.includes(pinType)) return;
						if (elementBBoxG.bottom > contentInnerBBox.bottom && PINS.BOTTOM.includes(pinType)) return;

						let distanceTop: number;
						let distanceBottom: number;

						if (PINS.CENTER.includes(pinType)) {
							distanceTop = elementBBoxL.top - elementBBoxL.height / 2;
							distanceBottom = elementBBoxL.top + elementBBoxG.height / 2;
						} else if (PINS.TOP.includes(pinType)) {
							distanceTop = elementBBoxL.top / 2;
							distanceBottom = (elementBBoxL.top + elementBBoxL.height) / 2;
						} else {
							distanceTop = (elementBBoxL.top - elementBBoxL.height) / 2;
							distanceBottom = elementBBoxL.top / 2;
						}

						if (!bounds) {
							bounds = {
								top: Math.min(distanceTop, -1 * distanceBottom),
								right: 0,
								bottom: Math.max(distanceBottom, -1 * distanceTop),
								left: 0,
							};
						}

						bounds.top = Math.min(bounds.top, Math.min(distanceTop, -1 * distanceBottom));
						bounds.bottom = Math.max(bounds.bottom, Math.max(distanceBottom, -1 * distanceTop));
						break;
					}

					case 'bottom': {
						if (elementBBoxG.bottom > contentInnerBBox.bottom && PINS.BOTTOM.includes(pinType)) return;
						if (elementBBoxG.top < contentInnerBBox.top && PINS.TOP.includes(pinType)) return;

						let distance: number;
						if (PINS.CENTER.includes(pinType)) {
							distance = (Math.abs(elementBBoxL.top) + elementBBoxL.height / 2) * 2;
						} else {
							distance = Math.abs(elementBBoxL.top) + elementBBoxL.height;
						}

						if (!bounds) {
							bounds = {
								top: contentInnerBBox.height - distance,
								right: 0,
								bottom: contentInnerBBox.height,
								left: 0,
							};
						}

						bounds.top = Math.min(bounds.top, contentInnerBBox.height - distance);
						break;
					}

					default:
						break;
				}
			});

			if (bounds) {
				return {
					top: bounds.top,
					right: bounds.right,
					bottom: bounds.bottom,
					left: bounds.left,
					width: bounds.right - bounds.left,
					height: bounds.bottom - bounds.top,
				};
			}
		}

		return null;
	};

	resizeContent = () => {
		if (!this.props.contentInnerRef.current) return;
		const contentBBox = this.getContentBBox();

		if (contentBBox) {
			this.props.contentInnerRef.current.style.height = convertPxToVw(contentBBox.height);
		}
	};

	render() {
		return this.props.children;
	}
}
